import {
    CloudAnalytics,
    CloudAnalyticsBreakdownRow,
    CloudAnalyticsCostPoint,
    CloudAnalyticsFact,
    CloudAnalyticsMonthPoint,
    CloudAnalyticsRiskRow,
    CloudAnalyticsTopResource,
} from '../../services/cloudAssetService';

/**
 * Cross-filtering for the Analytics → Cloud tab (User Story 100).
 *
 * The server ships one fact row per resource alongside its own aggregates; this
 * module re-derives every visual from those rows under a selection, so clicking
 * a slice costs nothing but a re-render. The aggregation deliberately mirrors
 * `cloud-analytics.service.ts` — a backend test asserts a rollup of the facts
 * reproduces the aggregates, so clearing a selection cannot change a number
 * that should not have changed.
 */

export type CloudDimension =
    | 'provider' | 'tier' | 'environment' | 'resourceType'
    | 'region' | 'state' | 'lifecycle' | 'department' | 'owner';

/** Selected keys per dimension. Within a dimension OR, across dimensions AND. */
export type CloudSelection = Partial<Record<CloudDimension, string[]>>;

/**
 * Registration date window, applied on top of the dimension selection.
 *
 * Not a dimension: no chart groups by it, so it is never excluded the way a
 * chart's own dimension is — a date range narrows every visual, including the
 * one whose slice you clicked.
 */
export interface CloudDateRange {
    startDate?: string;
    endDate?: string;
}

/** True when the row was registered inside the window (open-ended either side). */
export function withinRange(fact: CloudAnalyticsFact, range?: CloudDateRange): boolean {
    if (!range || (!range.startDate && !range.endDate)) return true;
    if (!fact.createdAt) return false;
    const created = fact.createdAt.slice(0, 10);
    if (range.startDate && created < range.startDate) return false;
    // Inclusive of the end date — a user picking 30 June means "through the 30th".
    if (range.endDate && created > range.endDate) return false;
    return true;
}

export const DIMENSION_LABELS: Record<CloudDimension, string> = {
    provider: 'Provider',
    tier: 'Environment',
    environment: 'Environment account',
    resourceType: 'Resource type',
    region: 'Region',
    state: 'State',
    lifecycle: 'Lifecycle',
    department: 'Business unit',
    owner: 'Owner',
};

export interface CloudAnalyticsView {
    kpis: CloudAnalytics['kpis'];
    byProvider: CloudAnalyticsBreakdownRow[];
    byEnvironmentTier: CloudAnalyticsBreakdownRow[];
    byEnvironment: CloudAnalyticsBreakdownRow[];
    byResourceType: CloudAnalyticsBreakdownRow[];
    byRegion: CloudAnalyticsBreakdownRow[];
    byProviderState: CloudAnalyticsBreakdownRow[];
    byLifecycleStatus: CloudAnalyticsBreakdownRow[];
    byDepartment: CloudAnalyticsBreakdownRow[];
    byOwner: CloudAnalyticsBreakdownRow[];
    createdOverTime: CloudAnalyticsMonthPoint[];
    costTrend: CloudAnalyticsCostPoint[];
    topCostlyResources: CloudAnalyticsTopResource[];
    riskBySeverity: CloudAnalyticsRiskRow[];
    idleResources: CloudAnalyticsTopResource[];
}

const labelKey = (dimension: CloudDimension): keyof CloudAnalyticsFact => (
    dimension === 'lifecycle' ? 'lifecycle' : (`${dimension}Label` as keyof CloudAnalyticsFact)
);

export const factLabel = (fact: CloudAnalyticsFact, dimension: CloudDimension): string =>
    String(fact[labelKey(dimension)] ?? fact[dimension]);

export const isSelected = (
    selection: CloudSelection,
    dimension: CloudDimension,
    key: string,
): boolean => (selection[dimension] ?? []).includes(key);

export const selectionCount = (selection: CloudSelection): number =>
    Object.values(selection).reduce((total, keys) => total + (keys?.length ?? 0), 0);

/**
 * Adds or removes one key. `additive` (ctrl/cmd-click) keeps the rest of the
 * dimension's keys; a plain click replaces them, which is what "select this
 * one" means to everyone who has not read a manual.
 */
export function toggleSelection(
    selection: CloudSelection,
    dimension: CloudDimension,
    key: string,
    additive: boolean,
): CloudSelection {
    const current = selection[dimension] ?? [];
    const next = current.includes(key)
        ? current.filter((k) => k !== key)
        : additive ? [...current, key] : [key];

    const updated: CloudSelection = { ...selection };
    if (next.length === 0) delete updated[dimension];
    else updated[dimension] = next;
    return updated;
}

/**
 * Rows matching every selected dimension except `exclude`.
 *
 * Excluding the visual's own dimension is what keeps a chart usable after you
 * click it: filter the provider donut by its own selection and it collapses to
 * a single slice with no way back to the others.
 */
export function applySelection(
    facts: CloudAnalyticsFact[],
    selection: CloudSelection,
    exclude?: CloudDimension,
    range?: CloudDateRange,
): CloudAnalyticsFact[] {
    const dimensions = (Object.keys(selection) as CloudDimension[])
        .filter((d) => d !== exclude && (selection[d]?.length ?? 0) > 0);
    const hasRange = !!(range && (range.startDate || range.endDate));
    if (dimensions.length === 0 && !hasRange) return facts;
    return facts.filter((fact) =>
        withinRange(fact, range) &&
        dimensions.every((d) => selection[d]!.includes(String(fact[d]))),
    );
}

function breakdown(
    facts: CloudAnalyticsFact[],
    dimension: CloudDimension,
): CloudAnalyticsBreakdownRow[] {
    const rows = new Map<string, CloudAnalyticsBreakdownRow>();
    facts.forEach((fact) => {
        const key = String(fact[dimension]);
        const cost = fact.monthlyCostBase ?? 0;
        const existing = rows.get(key);
        if (existing) {
            existing.count += 1;
            existing.monthlyCost += cost;
        } else {
            rows.set(key, { key, label: factLabel(fact, dimension), count: 1, monthlyCost: cost });
        }
    });
    return [...rows.values()].sort((a, b) => b.count - a.count);
}

/** Fact → the row shape both resource tables render. */
const toTopResource = (fact: CloudAnalyticsFact): CloudAnalyticsTopResource => ({
    id: fact.id,
    name: fact.name,
    provider: fact.providerLabel,
    resourceType: fact.resourceType === 'none' ? null : fact.resourceTypeLabel,
    environment: fact.environment === 'none' ? null : fact.environmentLabel,
    department: fact.department === 'none' ? null : fact.departmentLabel,
    environmentTier: fact.tier === 'none' ? null : fact.tierLabel,
    providerState: fact.state,
    monthlyCost: fact.monthlyCost ?? 0,
    currency: fact.currency,
    monthlyCostBase: fact.monthlyCostBase ?? 0,
});

export function aggregate(
    data: CloudAnalytics,
    selection: CloudSelection,
    range?: CloudDateRange,
): CloudAnalyticsView {
    const facts = data.facts;
    const scoped = applySelection(facts, selection, undefined, range);

    const severityOf = new Map(data.findingCatalog.map((f) => [f.id, f.severity]));
    const countState = (state: string) => scoped.filter((f) => f.state === state).length;
    const idle = scoped.filter(
        (f) => (f.state === 'stopped' || f.state === 'terminated') && f.lifecycle === 'active',
    );
    const withCost = scoped.filter((f) => f.monthlyCost != null);
    const compliant = scoped.filter((f) => f.findings.length === 0).length;

    const costAsOf = withCost
        .map((f) => f.costUpdatedAt)
        .filter((d): d is string => !!d)
        .sort()[0] ?? null;

    const riskBySeverity: CloudAnalyticsRiskRow[] = (['high', 'medium', 'low'] as const).map(
        (severity) => {
            let findingCount = 0;
            let resourceCount = 0;
            scoped.forEach((fact) => {
                const hits = fact.findings.filter((id) => severityOf.get(id) === severity).length;
                findingCount += hits;
                if (hits > 0) resourceCount += 1;
            });
            return { severity, findingCount, resourceCount };
        },
    );

    const createdOverTime: CloudAnalyticsMonthPoint[] = (() => {
        let cumulative = scoped.filter((f) => f.createdBeforeWindow).length;
        return data.trendMonths.map((month) => {
            const count = scoped.filter((f) => f.createdMonth === month).length;
            cumulative += count;
            return { month, count, cumulative };
        });
    })();

    const costTrend: CloudAnalyticsCostPoint[] = data.trendMonths.map((month, index) => {
        let monthlyCost = 0;
        let resourcesWithCost = 0;
        scoped.forEach((fact) => {
            const value = fact.costByMonth[index];
            if (value == null) return;
            monthlyCost += value;
            resourcesWithCost += 1;
        });
        return { month, monthlyCost, resourcesWithCost };
    });

    const byCostDesc = (a: CloudAnalyticsFact, b: CloudAnalyticsFact) =>
        (b.monthlyCostBase ?? 0) - (a.monthlyCostBase ?? 0);

    return {
        kpis: {
            ...data.kpis,
            totalResources: scoped.length,
            activeResources: scoped.filter((f) => f.lifecycle === 'active').length,
            inactiveResources: scoped.filter((f) => f.lifecycle !== 'active').length,
            running: countState('running'),
            stopped: countState('stopped'),
            terminated: countState('terminated'),
            unknownState: countState('unknown'),
            unownedResources: scoped.filter((f) => f.owner === 'none').length,
            distinctRegions: new Set(
                scoped.filter((f) => f.region !== 'none').map((f) => f.region),
            ).size,
            distinctProviders: new Set(scoped.map((f) => f.provider)).size,
            monthlyCost: scoped.reduce((total, f) => total + (f.monthlyCostBase ?? 0), 0),
            resourcesWithoutCost: scoped.filter((f) => f.monthlyCost == null).length,
            compliancePercent: scoped.length === 0 ? 100 : Math.round((compliant / scoped.length) * 100),
            compliantResources: compliant,
            nonCompliantResources: scoped.length - compliant,
            idleCandidates: idle.length,
            idleMonthlyCost: idle.reduce((total, f) => total + (f.monthlyCostBase ?? 0), 0),
            costAsOf,
            staleCostCount: scoped.filter((f) => f.staleCost).length,
            unconvertibleCurrencies: [
                ...new Set(
                    scoped
                        .filter((f) => f.monthlyCost != null && f.monthlyCostBase == null)
                        .map((f) => f.currency),
                ),
            ],
        },
        // Each breakdown ignores its own dimension's selection — see applySelection.
        byProvider: breakdown(applySelection(facts, selection, 'provider', range), 'provider'),
        byEnvironmentTier: breakdown(applySelection(facts, selection, 'tier', range), 'tier'),
        byEnvironment: breakdown(applySelection(facts, selection, 'environment', range), 'environment'),
        byResourceType: breakdown(applySelection(facts, selection, 'resourceType', range), 'resourceType'),
        byRegion: breakdown(applySelection(facts, selection, 'region', range), 'region'),
        byProviderState: breakdown(applySelection(facts, selection, 'state', range), 'state'),
        byLifecycleStatus: breakdown(applySelection(facts, selection, 'lifecycle', range), 'lifecycle'),
        byDepartment: breakdown(applySelection(facts, selection, 'department', range), 'department'),
        byOwner: breakdown(applySelection(facts, selection, 'owner', range), 'owner'),
        createdOverTime,
        costTrend,
        topCostlyResources: withCost.sort(byCostDesc).slice(0, 10).map(toTopResource),
        riskBySeverity,
        idleResources: [...idle].sort(byCostDesc).slice(0, 10).map(toTopResource),
    };
}

/** The server's own aggregates, for when the fact table was withheld. */
export function serverView(data: CloudAnalytics): CloudAnalyticsView {
    const { kpis, byProvider, byEnvironmentTier, byEnvironment, byResourceType, byRegion,
        byProviderState, byLifecycleStatus, byDepartment, byOwner, createdOverTime,
        costTrend, topCostlyResources, riskBySeverity, idleResources } = data;
    return {
        kpis, byProvider, byEnvironmentTier, byEnvironment, byResourceType, byRegion,
        byProviderState, byLifecycleStatus, byDepartment, byOwner, createdOverTime,
        costTrend, topCostlyResources, riskBySeverity, idleResources,
    };
}
